import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Upload, Send, CheckCircle, AlertTriangle, Mail, Clock } from 'lucide-react';
import { useToast } from '../ui/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ApplicationSection = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    role: '',
    country: '',
    customCountry: '',
    monthlyQuantity: '',
    goldPurity: '',
    refinery: '',
    customRefinery: '',
    collateral: '',
    whatsapp: '',
    email: '',
    message: ''
  });

  const countries = [
    { name: 'Uganda', flag: '🇺🇬' },
    { name: 'Kenya', flag: '🇰🇪' },
    { name: 'DRC', flag: '🇨🇩' },
    { name: 'Other', flag: '🌍' }
  ];

  const refineries = [
    'Feildstein',
    'Other'
  ];

  const purityRanges = ['85-90%', '90-95%', '95-98%', '98%+', 'Not yet tested'];
  const quantityRanges = ['1-5 kg', '5-10 kg', '10-25 kg', '25-50 kg', '50-100 kg', '100+ kg'];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      // Basic validation
      if (!formData.fullName || !formData.email || !formData.role || !formData.whatsapp) {
        throw new Error('Please fill in all required fields');
      }
      
      // Additional validation for custom fields
      if (formData.country === 'Other' && !formData.customCountry) {
        throw new Error('Please specify your country');
      }
      
      if (formData.refinery === 'Other' && !formData.customRefinery) {
        throw new Error('Please specify your refinery');
      }

      // Prepare submission data
      const submissionData = {
        ...formData,
        country: formData.country === 'Other' ? formData.customCountry : formData.country,
        refinery: formData.refinery === 'Other' ? formData.customRefinery : formData.refinery
      };

      // Real API call
      const response = await axios.post(`${API}/applications`, submissionData);
      
      if (response.data.success) {
        // Store the submitted email for the confirmation modal
        setSubmittedEmail(formData.email);
        
        // Show confirmation modal instead of toast
        setShowConfirmationModal(true);

        // Reset form
        setFormData({
          fullName: '',
          role: '',
          country: '',
          customCountry: '',
          monthlyQuantity: '',
          goldPurity: '',
          refinery: '',
          customRefinery: '',
          collateral: '',
          whatsapp: '',
          email: '',
          message: ''
        });
      } else {
        throw new Error(response.data.message || 'Application failed');
      }

    } catch (error) {
      toast({
        title: "Application Failed",
        description: error.response?.data?.message || error.message || 'Please try again later',
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="application" className="py-24 bg-[#040F2D]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-6">Apply Now</h2>
          <p className="text-xl text-white/70 mb-8">
            Join our verified seller network
          </p>
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 inline-block">
            <div className="flex items-center text-red-700">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <span className="font-semibold">Licensed sellers and official mandates only</span>
            </div>
          </div>
        </div>

        <Card className="border-0 shadow-2xl bg-gradient-to-br from-white to-gray-50">
          <CardHeader className="bg-gradient-to-r from-[#040F2D] to-[#0B132B] text-white rounded-t-lg">
            <CardTitle className="text-3xl text-center">Application Form</CardTitle>
          </CardHeader>
          <CardContent className="p-10">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Full Name */}
              <div className="space-y-3">
                <Label htmlFor="fullName" className="text-lg font-semibold text-white">Full Name *</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={formData.fullName}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  className="form-input h-12 text-lg"
                  required
                />
              </div>

              {/* Role */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold text-white">Your Role *</Label>
                <RadioGroup 
                  value={formData.role} 
                  onValueChange={(value) => handleInputChange('role', value)}
                  className="flex flex-col space-y-4"
                >
                  <div className="flex items-center space-x-3 p-4 border border-white/10 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value="producer" id="producer" />
                    <Label htmlFor="producer" className="text-lg">Artisanal Gold Producer</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-4 border border-white/10 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value="mandate" id="mandate" />
                    <Label htmlFor="mandate" className="text-lg">Official Mandate</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Country */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold text-white">Country of Origin *</Label>
                <Select onValueChange={(value) => handleInputChange('country', value)}>
                  <SelectTrigger className="form-input h-12 text-lg bg-white/5 border border-white/20 text-white">
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B132B]/60">
                    {countries.map((country) => (
                      <SelectItem key={country.name} value={country.name} className="hover:bg-gray-100">
                        <span className="flex items-center gap-2">
                          <span>{country.flag}</span>
                          <span>{country.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Custom Country Input */}
                {formData.country === 'Other' && (
                  <div className="mt-3">
                    <Label htmlFor="customCountry" className="text-lg font-semibold text-white">Please specify your country *</Label>
                    <Input
                      id="customCountry"
                      type="text"
                      value={formData.customCountry}
                      onChange={(e) => handleInputChange('customCountry', e.target.value)}
                      className="form-input h-12 text-lg mt-2"
                      placeholder="Enter your country name"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Monthly Quantity & Purity in grid */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <Label className="text-lg font-semibold text-white">Monthly Quantity *</Label>
                  <Select onValueChange={(value) => handleInputChange('monthlyQuantity', value)}>
                    <SelectTrigger className="form-input h-12 bg-white/5 border border-white/20 text-white">
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B132B]/60">
                      {quantityRanges.map((range) => (
                        <SelectItem key={range} value={range} className="hover:bg-gray-100">
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <Label className="text-lg font-semibold text-white">Gold Purity *</Label>
                  <Select onValueChange={(value) => handleInputChange('goldPurity', value)}>
                    <SelectTrigger className="form-input h-12 bg-white/5 border border-white/20 text-white">
                      <SelectValue placeholder="Select purity" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0B132B]/60">
                      {purityRanges.map((range) => (
                        <SelectItem key={range} value={range} className="hover:bg-gray-100">
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Refinery */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold text-white">Refinery Partner *</Label>
                <Select onValueChange={(value) => handleInputChange('refinery', value)}>
                  <SelectTrigger className="form-input h-12 bg-white/5 border border-white/20 text-white">
                    <SelectValue placeholder="Select refinery type" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0B132B]/60">
                    {refineries.map((refinery) => (
                      <SelectItem key={refinery} value={refinery} className="hover:bg-gray-100">
                        {refinery}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Custom Refinery Input */}
                {formData.refinery === 'Other' && (
                  <div className="mt-3">
                    <Label htmlFor="customRefinery" className="text-lg font-semibold text-white">Please specify your refinery *</Label>
                    <Input
                      id="customRefinery"
                      type="text"
                      value={formData.customRefinery}
                      onChange={(e) => handleInputChange('customRefinery', e.target.value)}
                      className="form-input h-12 text-lg mt-2"
                      placeholder="Enter refinery name"
                      required
                    />
                  </div>
                )}
              </div>

              {/* Collateral */}
              <div className="space-y-3">
                <Label className="text-lg font-semibold text-white">Collateral Availability *</Label>
                <RadioGroup 
                  value={formData.collateral} 
                  onValueChange={(value) => handleInputChange('collateral', value)}
                  className="flex flex-col space-y-3"
                >
                  <div className="flex items-center space-x-3 p-3 border border-white/10 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value="yes" id="yes" />
                    <Label htmlFor="yes" className="text-lg">Yes</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-white/10 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value="no" id="no" />
                    <Label htmlFor="no" className="text-lg">No</Label>
                  </div>
                  <div className="flex items-center space-x-3 p-3 border border-white/10 rounded-lg hover:bg-gray-50">
                    <RadioGroupItem value="discuss" id="discuss" />
                    <Label htmlFor="discuss" className="text-lg">To be discussed</Label>
                  </div>
                </RadioGroup>
              </div>

              {/* Contact in grid */}
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-3">
                  <Label htmlFor="whatsapp" className="text-lg font-semibold text-white">WhatsApp *</Label>
                  <Input
                    id="whatsapp"
                    type="tel"
                    placeholder="+256 XXX XXX XXX"
                    value={formData.whatsapp}
                    onChange={(e) => handleInputChange('whatsapp', e.target.value)}
                    className="form-input h-12"
                    required
                  />
                </div>

                <div className="space-y-3">
                  <Label htmlFor="email" className="text-lg font-semibold text-white">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className="form-input h-12"
                    required
                  />
                </div>
              </div>

              {/* Optional Message */}
              <div className="space-y-3">
                <Label htmlFor="message" className="text-lg font-semibold text-white">Additional Information (Optional)</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  className="form-input min-h-[100px]"
                  placeholder="Any additional information about your gold mining operation..."
                />
              </div>

              {/* Submit Button */}
              <div className="pt-8">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#040F2D] to-[#0B132B] hover:from-[#0B132B] hover:to-[#040F2D] text-white py-6 text-xl font-bold shadow-xl hover:shadow-2xl transition-all transform hover:scale-105"
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white mr-3"></div>
                      Submitting Application...
                    </>
                  ) : (
                    <>
                      <Send className="mr-3 h-6 w-6" />
                      Submit Application
                    </>
                  )}
                </Button>
              </div>

              <div className="text-center text-sm text-white/70 mt-6">
                <p>✉️ You will receive a confirmation email at your provided email address</p>
                <p>📱 Our team will contact you within 48 hours via WhatsApp</p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Confirmation Modal */}
        <Dialog open={showConfirmationModal} onOpenChange={setShowConfirmationModal}>
          <DialogContent className="max-w-md mx-4 sm:max-w-lg">
            <DialogHeader className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <DialogTitle className="text-2xl font-bold text-white">
                Application Received!
              </DialogTitle>
              <DialogDescription className="text-lg text-white/70 space-y-4">
                <div className="space-y-3">
                  <p className="font-semibold">
                    Thank you! We have successfully received your application.
                  </p>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center text-blue-700 mb-2">
                      <Mail className="h-5 w-5 mr-2" />
                      <span className="font-semibold">Check Your Email</span>
                    </div>
                    <p className="text-blue-600 text-sm">
                      A confirmation email has been sent to <strong>{submittedEmail}</strong>
                    </p>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-center text-yellow-700 mb-2">
                      <Clock className="h-5 w-5 mr-2" />
                      <span className="font-semibold">Next Steps</span>
                    </div>
                    <p className="text-yellow-600 text-sm">
                      Our team will contact you within <strong>48 hours</strong> via WhatsApp to proceed with the verification process.
                    </p>
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex justify-center pt-4">
              <Button 
                onClick={() => setShowConfirmationModal(false)}
                className="bg-gradient-to-r from-[#040F2D] to-[#0B132B] hover:from-[#0B132B] hover:to-[#040F2D] text-white px-8 py-2"
              >
                Got it
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default ApplicationSection;